import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { TreeModel } from "@components/tree/tree.model";
import * as _ from 'lodash';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Log } from "ng2-logger";
import { TreeAdapter } from "@app/shared/util/tree-adapter.utils";
import { ScannerDirective } from "@app/shared/directives/scanner.directive";
import { ToolSet } from "@app/tool/model/toolSet.model";
import { Tool } from "@app/tool/model/tool.model";

@Component({
    selector: 'app-tool-inspect-tree',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ToolInspectTreeComponent,
            multi: true
        }
    ],
    templateUrl: './tool-inspect-tree.component.html',
    styleUrls: ['./tool-inspect-tree.component.scss']
})
export class ToolInspectTreeComponent implements OnInit, ControlValueAccessor {
    _logger = Log.create(ToolInspectTreeComponent.name);

    @Input() loading = false;
    @Input() showErrors = false;
    @Input() formControl: UntypedFormControl;
    @Input() labelText = '';
    @Input() emptyMessage = '';
    @Input() propagateSelectionUp = true;
    @Input() isMaterial = true;
    @Input() isScanListenerActive = false;
    @Input() noToolsMessage = '';
    @Input() isScanEnabled = true;

    @Output() toolSelect = new EventEmitter<any>();
    @Output() toolUnselect = new EventEmitter<any>();
    @Output() toolsSelected = new EventEmitter<any>();
    @Output() rowAlertClick = new EventEmitter<any>();
    @Output() itemNotFound = new EventEmitter<any>();

    selectedTools = [];
    focused = true;
    _items: TreeModel[] = [];

    // strobe for scaning functionality to add tools to table on scan
    scanEnabled = false;
    // strobe for inspection mode in which tools are striked out after they are scanned
    inspectEnabled = false;


    private onChangeCallback: (_: any) => {};
    private onTouchedCallback: (_: any) => {};

    constructor(private toastr: ToastsManager,
                private treeAdapter: TreeAdapter,
                private translateService: TranslateService) {
    }

    @HostListener('window:focus', ['$event'])
    onFocus() {
        this.focused = true;
    }

    @HostListener('window:blur', ['$event'])
    onBlur() {
        this.focused = false;
    }

    ngOnInit() {
    }

    onScan(toolUid) {
        this.loading = true;
        const dataMatrix = ScannerDirective.UID_REGEX.exec(toolUid);
        const uid = _.toLower(dataMatrix[3]);

        if (!this.isToolAlreadyInList(uid)) {
            this._logger.info('onScan:: Tool or set not found in the return request');
            this.itemNotFound.emit(uid);
            this.loading = false;
        }

        this._logger.info('onScan:: Confirming inspection on tool with uid', uid);
        const toolOrSet: TreeModel = _.find(this.items, (item) => {
            if (item.data instanceof Tool) {
                return item.data.toolUid === uid;
            } else if (item.data instanceof ToolSet) {
                const linearizedData = item.data.tools;
                const result = _.chain(linearizedData)
                    .filter({toolUid: uid})
                    .value();
                return result.length > 0;
            }
        });
        this.loading = false;
        this.makeSelections(toolOrSet, uid);
    }

    onToolSelect(event) {
        this._logger.info('onToolSelect:: event', event);
        this.emitSelection(event.node);
    }

    onToolUnselect(event) {
        this._logger.info('onToolUnselect:: event', event);
        this.emitUnselection(event.node);
    }

    onRowAlertClick(event) {
        this._logger.info('onRowAlertClick:: event', event);
        this.rowAlertClick.emit(event);
    }

    onSelections(selections: TreeModel[]) {
        this._logger.info('onSelections:: List of selected items', selections);
        this.selectedTools = selections;

        if (this.onChangeCallback) {
            this.onChangeCallback(this.selectedTools);
        }
        this.toolsSelected.emit(this.selectedTools);
    }

    get items() {
        return this._items;
    }

    @Input()
    set items(items) {
        this._items = this.treeAdapter.adaptToolsToTree(items, 'tool-inspect');
        let flatItems = [];
        const sets = [];
        this.items.forEach(item => {
            if (item.children && item.children.length > 0) {
                flatItems = flatItems.concat(item.children);
                sets.push(item);
            } else {
                flatItems.push(item);
            }
        });

        flatItems.forEach(item => {
            const selectedItem = item.data.isScanned ? item : null;

            if (selectedItem) {
                selectedItem.inspected = true;
                this.selectedTools.push(selectedItem);
                this.onSelections(this.selectedTools);
            }
        });

        // TODO XNECAZ try an do this in less iterations
        sets.forEach(set => {
            const selected = _.find(set.children, item => {
                return item.data.isScanned === false;
            });

            if (!selected) {
                this.selectedTools.push(set);
            }
        });

        this.inspectEnabled = true;
        this.scanEnabled = this.isScanEnabled;
    }

    writeValue(obj: any): void {
        this.selectedTools = obj || [];
    }

    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    shouldRenderScanStatus() {
        return this.isScanEnabled === true;
    }

    private makeSelections(toolOrSet: TreeModel, uid: string) {
        let selectedItem: TreeModel;
        if (toolOrSet && toolOrSet.children && toolOrSet.children.length > 0) {
            selectedItem = _.find(toolOrSet.children, function (item) {
                return item.data.toolUid === uid;
            });
        } else {
            selectedItem = _.find(this.items, function (item) {
                return item.data.toolUid === uid;
            });
        }

        if (this.selectedTools.indexOf(selectedItem) !== -1) {
            this.toastr.warning(this.buildToolAlreadyExistentMessage(selectedItem.data.toolUid));
            return;
        }

        if (selectedItem) {
            this.selectedTools.push(selectedItem);
            if (selectedItem.parent) {
                selectedItem.parent.partialSelected = true;
                if (this.selectedTools.filter(x => x.parent && x.parent.data && x.parent.data.id === selectedItem.parent.data.id).length
                    === selectedItem.parent.children.length) {
                    selectedItem.parent.partialSelected = false;
                    this.selectedTools.push(selectedItem.parent);
                }
            }
            this.emitSelection(selectedItem);
            this.onSelections(this.selectedTools);
        }
    }

    private isToolAlreadyInList(uid) {

        if (this.items && this.items.length === 0) {
            return false;
        }


        let result = _.chain(this.items)
            .map('data')
            .value();
        result = this.liniarizeToolSets(result);

        const finalResult = _.filter(result, {toolUid: uid});

        return finalResult.length > 0;
    }

    private liniarizeToolSets(items) {
        let newDatas = [];
        if (items) {
            items.forEach((item) => {
                if (item instanceof Tool) {
                    newDatas.push(item);
                } else if (item instanceof ToolSet) {
                    newDatas = newDatas.concat(item.tools);
                }
            });
        }
        return newDatas;
    }

    private buildToolAlreadyExistentMessage(uid) {
        return this.translateService.instant('tree.toolSelect.toasts.alreadyScanned', {uid: uid});
    }

    private emitSelection(change: TreeModel) {
        this._logger.info('emitSelection:: ', change);
        if (this.onChangeCallback) {
            this.onChangeCallback(this.selectedTools);
        }
        this.toolSelect.emit(change);
    }

    private emitUnselection(change: TreeModel) {
        this._logger.info('emitUnselection:: ', change);
        if (this.onChangeCallback) {
            this.onChangeCallback(this.selectedTools);
        }
        this.toolUnselect.emit(change);
    }
}
